import { useState } from 'react'
import { BookOpen } from 'lucide-react'
import { combineQueryStates, safeHttpUrl, SectionHeader } from '@loop3/ui'
import { formatShortDate, parseYmd } from '@loop3/dates'
import type { JournalEntry, JournalWeek } from '@loop3/universe'
import { useClientJournal } from '@/hooks/useClientJournal'
import { journalWindow } from '@/lib/compute/client-journal'

const EMPTY_WEEKS: JournalWeek[] = []
const SOURCE_LABELS = { slack: 'Slack', meeting: 'Meeting' } as const

const shortDate = (ymd: string) =>
  formatShortDate(parseYmd(ymd), { locale: 'en-GB' })

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`

function countsLine({
  meetings,
  slack_threads,
  decisions,
}: JournalWeek['counts']) {
  return [
    meetings > 0 && plural(meetings, 'meeting'),
    slack_threads > 0 && plural(slack_threads, 'Slack thread'),
    decisions > 0 && plural(decisions, 'decision'),
  ]
    .filter(Boolean)
    .join(' · ')
}

/** The nightly client journal: one card per week with the weekly paragraph and expandable dated entries; renders nothing when empty. */
export function ClientJournal({ clientId }: { clientId: string }) {
  const journal = useClientJournal(clientId)
  const { status } = combineQueryStates([journal])
  const weeks = journal.data ?? EMPTY_WEEKS
  if (status !== 'ready' || weeks.length === 0) return null

  const { thisWeek } = journalWindow(new Date())
  return (
    <section>
      <p className="eyebrow mb-3 opacity-50">Updated nightly</p>
      <div className="space-y-3">
        {weeks.map((w) => (
          <JournalWeekCard
            key={w.weekStart}
            week={w}
            current={w.weekStart === thisWeek}
          />
        ))}
      </div>
    </section>
  )
}

/** Only the current ART week's card, with a link to the full journal; renders nothing when that week has no entry. */
export function CurrentWeekJournal({
  clientId,
  onOpenJournal,
}: {
  clientId: string
  onOpenJournal: () => void
}) {
  const journal = useClientJournal(clientId)
  const { thisWeek } = journalWindow(new Date())
  const week = (journal.data ?? EMPTY_WEEKS).find(
    (w) => w.weekStart === thisWeek
  )
  if (!week) return null
  return (
    <section>
      <SectionHeader
        icon={BookOpen}
        label="Journal"
        className="items-center"
        meta={
          <button
            type="button"
            onClick={onOpenJournal}
            className="eyebrow opacity-60 hover:text-[var(--l3-violet)] hover:opacity-100"
          >
            Full journal →
          </button>
        }
      />
      <JournalWeekCard week={week} current />
    </section>
  )
}

export function JournalWeekCard({
  week,
  current,
}: {
  week: JournalWeek
  current: boolean
}) {
  const [open, setOpen] = useState(false)
  const counts = countsLine(week.counts)
  const n = week.entries.length
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="eyebrow opacity-50">
        {current ? 'This week' : `Week of ${shortDate(week.weekStart)}`}
      </div>
      {week.paragraph && (
        <p className="mt-2 text-sm leading-relaxed">{week.paragraph}</p>
      )}
      <div className="mt-3 flex items-center justify-between gap-4">
        <span className="font-mono text-xs opacity-50">{counts}</span>
        {n > 0 && (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="text-xs text-[var(--l3-violet)] hover:underline"
          >
            {open ? 'Hide entries' : `Show ${plural(n, 'entry', 'entries')}`}
          </button>
        )}
      </div>
      {open && (
        <div className="mt-3 border-t border-border">
          {week.entries.map((e) => (
            <EntryRow key={e.id} entry={e} />
          ))}
        </div>
      )}
    </div>
  )
}

function EntryRow({ entry: e }: { entry: JournalEntry }) {
  return (
    <div className="grid grid-cols-[5.5rem_1fr] gap-4 border-b border-border/60 py-3 last:border-b-0">
      <span className="font-mono text-xs opacity-50">{shortDate(e.day)}</span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {e.sources.map((s) => (
            <span
              key={s}
              className="eyebrow rounded border border-border px-1.5 py-0.5 opacity-60"
            >
              {SOURCE_LABELS[s]}
            </span>
          ))}
          <span className="text-sm font-medium">{e.title}</span>
        </div>
        <p className="mt-1 text-xs leading-relaxed opacity-70">{e.summary}</p>
        <div className="mt-1.5 flex flex-wrap gap-3">
          {e.links.map((l) => {
            const href = safeHttpUrl(l.url)
            return href ? (
              <a
                key={l.url}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[var(--l3-violet)] hover:underline"
              >
                {l.label} ↗
              </a>
            ) : null
          })}
        </div>
      </div>
    </div>
  )
}
